import React, { useState } from 'react';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  PlayArrowRounded as PlayCircleRoundedIcon,
  VolumeMuteRounded as VolumeMuteRoundedIcon,
  VolumeOff as VolumeOffIcon,
  VolumeUpRounded as VolumeUpRoundedIcon,
  FlagRounded as FlagRoundedIcon,
  PauseOutlined as PauseOutlinedIcon,
  Forward5Rounded,
  SlowMotionVideoRounded as SlowMotionVideoRoundedIcon,
  Replay5Rounded,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import apis from '@src/apis';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { AUDIO_EFFECTS_URL, LANDING_PAGE_URL } from '@src/configs';
import { formatAudioTime } from '@src/utils/time';
import Tooltip from '@src/components/Tooltip';
import { StyledSidebarTooltip } from '@src/containers/Layout/SidebarNew/index.style';
import {
  AUDIO_MODE,
  EXTRACTION_STATUS,
  PROJECT_PRODUCT,
  VALID_VOLUME,
} from '@src/constants/project';
import actions from '@src/redux/actions';
import CircularProcess from '@src/components/CircularProcess';
import { StyledAudioPlayer, StyledSlider, StyledVolume } from './index.style';
import AudioSlider from './AudioSlider';
import VolumeMixerPopover from './VolumeMixerPopover';

const REWIND_TIME_IN_SECOND = 5;

const AudioPlayer = ({
  enableCreditUsagePreview,
  costsCredit,
  handleChangeVolume,
  isDisableConvertBtn,
  isPlayAudio,
  handleConvertAudio,
  isConverting,
  isLoading,
  isPlaying,
  currentTime,
  duration,
  handlePlay,
  handlePause,
  handleSeeked,
  title,
  onSaveProject,
  synthesisProgress,
  isDisabledBtnAudioEffects,
  product = PROJECT_PRODUCT.TTS,
  isPreProcessingSynthesis = false,
  isRewriting = false,
  showVideoVolumeControl = false,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const location = useLocation();
  const projectId = location.pathname.split('/')[2];
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [volume, setVolume] = useState(100);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const handleOpenChangeVolume = (e) => setAnchorEl(e.currentTarget);
  const handleCloseChangeVolume = () => setAnchorEl(null);

  const { id: userId } = useSelector((state) => state.auth.user);
  const isBuffering = useSelector((state) => state.project.isBuffering);
  const isGeneratingNarration = useSelector(
    (state) => state.project.isGeneratingNarration,
  );
  const source = useSelector((state) => state.project.source);
  // Block converting to audio while the project is still being prepared:
  // loading, extracting the source, or generating AI narration.
  const isExtracting =
    source?.extraction?.response?.status === EXTRACTION_STATUS.PENDING;
  const isConvertButtonUnavailable = isDisableConvertBtn || isRewriting;
  const isConvertButtonDisabled =
    isConvertButtonUnavailable || isGeneratingNarration || isExtracting;

  const handleConvertClick = () => {
    if (isConvertButtonDisabled) return;
    handleConvertAudio?.();
  };

  const renderConvertIcon = () => {
    if (isPreProcessingSynthesis) {
      return (
        <div className="audio-control-icon">
          <CircularProgress color="blueV2" size={42} thickness={4} />
        </div>
      );
    }
    if (isConverting) {
      return (
        <div className="audio-control-icon">
          <CircularProcess
            color="blueV2"
            size={42}
            textSize={11}
            value={synthesisProgress || 0}
            thickness={4}
          />
        </div>
      );
    }
    return (
      <Tooltip
        sx={{ display: isConvertButtonUnavailable ? 'none' : 'block' }}
        arrow
        title={
          enableCreditUsagePreview ? (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: t('createProjectCostsCredit', {
                  value: costsCredit?.toLocaleString(language),
                }),
              }}
            />
          ) : (
            t('convertAllBlockToAudio')
          )
        }
        placement="top"
      >
        <div className={isConvertButtonDisabled ? 'disabled' : ''}>
          <IconButton
            onClick={handleConvertClick}
            disabled={isConvertButtonDisabled}
          >
            <SlowMotionVideoRoundedIcon className="convert-icon" />
          </IconButton>
        </div>
      </Tooltip>
    );
  };

  const renderPlayOrPause = () => {
    if (isLoading || isBuffering) {
      return (
        <div className="audio-control-loading-container">
          <CircularProgress className="audio-control-loading" size={24} />
        </div>
      );
    }
    // Disable playback while AI narration is being generated.
    const disabledStyle = isGeneratingNarration
      ? { opacity: 0.3, cursor: 'not-allowed' }
      : undefined;
    if (isPlaying) {
      return (
        <div className="audio-control-icon">
          <Tooltip arrow title={t('pause')} placement="top">
            <PauseOutlinedIcon
              className="audio-control-icon-pause"
              onClick={isGeneratingNarration ? undefined : handlePause}
              style={disabledStyle}
            />
          </Tooltip>
        </div>
      );
    }
    return (
      <div className="audio-control-icon">
        <Tooltip arrow title={t('playAllAudio')} placement="top">
          <PlayCircleRoundedIcon
            className="audio-control-icon-play"
            onClick={isGeneratingNarration ? undefined : handlePlay}
            style={disabledStyle}
          />
        </Tooltip>
      </div>
    );
  };

  const handleOpenAudioEffects = async () => {
    if (isDisabledBtnAudioEffects) return;
    try {
      setIsGeneratingAudio(true);
      await onSaveProject();
      dispatch(actions.project.updateOriginalBlocks());
      const response = await apis.project.getJoinedAudio(
        projectId,
        AUDIO_MODE.JOINED,
      );
      const audioJoined = response?.result?.audioLink;
      if (response.status && audioJoined) {
        window.open(
          `${AUDIO_EFFECTS_URL}/?response-cache-control=no-cache&audio=${audioJoined}`,
          '_blank',
        );
      }
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      userId,
      projectId,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.PROJECT}`,
      '_blank',
    );
  };

  return (
    <StyledAudioPlayer>
      {isPlayAudio && !isLoading ? (
        <AudioSlider
          currentTime={currentTime / 1000}
          duration={duration / 1000}
          onSeeked={(value) => handleSeeked(value * 1000)}
          disabled={false}
        />
      ) : (
        <div className="audio-progress-bar" />
      )}
      <div className="audio-control-panel">
        <div className="audio-timer">
          <Typography className="project-title">{title}</Typography>
          <Typography className="timer">
            {formatAudioTime(currentTime)} /{' '}
            {formatAudioTime(isPlayAudio && !isLoading ? duration : 0)}
          </Typography>
        </div>
        <div className="audio-control">
          <div className="audio-control-icon">
            <Tooltip arrow title={t('rewind', { second: 5 })} placement="top">
              <div>
                <IconButton
                  disabled={!isPlayAudio || isLoading || currentTime === 0}
                  className="rewind-button"
                  color="iconPrimary"
                  onClick={() => {
                    const time = currentTime - REWIND_TIME_IN_SECOND;
                    handleSeeked(Math.max(0, time));
                  }}
                  data-id="skip-back-audio"
                >
                  <Replay5Rounded sx={{ fontSize: 26 }} />
                </IconButton>
              </div>
            </Tooltip>
          </div>
          {isPlayAudio ? renderPlayOrPause() : renderConvertIcon()}
          <div className="audio-control-icon">
            <Tooltip arrow title={t('forward', { second: 5 })} placement="top">
              <div>
                <IconButton
                  disabled={
                    !isPlayAudio || isLoading || currentTime >= duration
                  }
                  className="rewind-button"
                  color="iconPrimary"
                  onClick={() =>
                    handleSeeked(
                      Math.min(currentTime + REWIND_TIME_IN_SECOND, duration),
                    )
                  }
                  data-id="skip-forward-audio"
                >
                  <Forward5Rounded sx={{ fontSize: 26 }} />
                </IconButton>
              </div>
            </Tooltip>
          </div>
        </div>
        <div className="audio-config">
          {product === PROJECT_PRODUCT.TTS && (
            <div className="audio-config-icon">
              {isGeneratingAudio ? (
                <IconButton style={{ cursor: 'default' }} className="disabled">
                  <CircularProgress
                    size={24}
                    className="audio-config-icon-loading"
                    color="iconPrimary"
                    thickness={4}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={t('effectGuide')}
                  arrow
                  disableHoverListener={isDisabledBtnAudioEffects}
                >
                  <IconButton
                    onClick={handleOpenAudioEffects}
                    className={isDisabledBtnAudioEffects ? 'disabled' : ''}
                  >
                    <TuneRoundedIcon
                      sx={{ fontSize: 24 }}
                      color="iconPrimary"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}
          <div className="audio-config-icon">
            <StyledSidebarTooltip title={t('volume')} placement="top">
              <div>
                <IconButton
                  data-id="open-volume-at-player"
                  onClick={handleOpenChangeVolume}
                  className={anchorEl ? 'button-active' : ''}
                >
                  {volume ? (
                    <VolumeUpRoundedIcon
                      sx={{ fontSize: 24 }}
                      color="iconPrimary"
                    />
                  ) : (
                    <VolumeOffIcon sx={{ fontSize: 24 }} color="iconPrimary" />
                  )}
                </IconButton>
              </div>
            </StyledSidebarTooltip>
          </div>
          <div className="audio-config-icon">
            <Tooltip title={t('errorReport')} arrow>
              <IconButton onClick={handleOpenErrorReport}>
                <FlagRoundedIcon sx={{ fontSize: 24 }} color="iconPrimary" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
      {showVideoVolumeControl ? (
        <VolumeMixerPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseChangeVolume}
          audioVolume={volume}
          onChangeAudioVolume={(value) => {
            handleChangeVolume(value);
            setVolume(value);
          }}
        />
      ) : (
        <StyledVolume
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseChangeVolume}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <div className="volume-wrapper">
            <IconButton
              onClick={() => {
                handleChangeVolume(VALID_VOLUME.MAX);
                setVolume(VALID_VOLUME.MAX);
              }}
            >
              <VolumeUpRoundedIcon sx={{ fontSize: 24 }} color="iconPrimary" />
            </IconButton>
            <StyledSlider
              sx={{
                '& input[type="range"]': {
                  WebkitAppearance: 'slider-vertical',
                },
              }}
              orientation="vertical"
              value={volume}
              min={VALID_VOLUME.MIN}
              max={VALID_VOLUME.MAX}
              step={1}
              onChange={(e, value) => {
                handleChangeVolume(value);
                setVolume(value);
              }}
            />
            <IconButton
              onClick={() => {
                handleChangeVolume(VALID_VOLUME.MIN);
                setVolume(VALID_VOLUME.MIN);
              }}
            >
              <VolumeMuteRoundedIcon
                sx={{ fontSize: 24 }}
                color="iconPrimary"
              />
            </IconButton>
          </div>
        </StyledVolume>
      )}
    </StyledAudioPlayer>
  );
};

export default AudioPlayer;
